import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { PortalHeading } from './PortalHeading';
import { IS_IO } from 'lib/site';

type SettingsView = 'plan' | 'subscription' | 'history' | 'referrals' | 'profile';

const VIEWS: { view: SettingsView; to: string; labelKey: string; end?: boolean; io: boolean }[] = [
  { view: 'plan', to: '/portal/settings/plan', labelKey: 'studyPlan.tab', io: false },
  { view: 'subscription', to: '/portal/settings/subscription', labelKey: 'subscription.tab', io: false },
  { view: 'history', to: '/portal/settings/history', labelKey: 'portal.results_history', io: true },
  { view: 'referrals', to: '/portal/settings/referrals', labelKey: 'referral.tab', io: false },
  { view: 'profile', to: '/portal/settings', labelKey: 'profile', end: true, io: true },
];

/** Settings: a left list of views on desktop, a scrolling row of pills on phones. Each view is its own route. */
export default function PortalSettings() {
  const { t } = useTranslation();
  const views = VIEWS.filter((item) => !IS_IO || item.io);

  return (
    <div className="flex flex-col">
      <PortalHeading title={t('settings')} />
      <div className="mt-5 grid gap-5 md:mt-7 md:grid-cols-[200px_minmax(0,1fr)] md:items-start md:gap-9 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav
          aria-label={t('settings')}
          className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-col md:gap-1 md:overflow-visible md:p-0"
        >
          {views.map(({ view, to, labelKey, end }) => (
            <NavLink
              key={view}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors lg:min-h-0',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-foreground text-background'
                    : 'border border-border bg-card text-foreground/70 hover:text-foreground md:border-transparent md:bg-transparent md:hover:bg-muted',
                )
              }
            >
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
